// ./src/hooks/useLookupDictionaries.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  LookupDictionary,
  LookupDictionaryService,
  LookupDictionaryFilter,
  LookupDictionaryInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseLookupDictionariesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<LookupDictionaryFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;
}

export interface UseLookupDictionariesReturn {
  // Data state
  lookupDictionary: LookupDictionary | null;
  lookupDictionaries: LookupDictionary[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<LookupDictionaryFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedLookupDictionary: LookupDictionary | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<LookupDictionaryFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    shouldReset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<LookupDictionary[]>;
  create: (params: LookupDictionaryInput) => Promise<LookupDictionary | null>;
  update: (
    id: string,
    params: LookupDictionaryInput
  ) => Promise<LookupDictionary | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // UI state management
  setSelectedLookupDictionary: (
    lookupDictionary: LookupDictionary | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useLookupDictionaries = (
  options: UseLookupDictionariesOptions = {}
): UseLookupDictionariesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Data state
  const [lookupDictionary, setLookupDictionary] =
    useState<LookupDictionary | null>(null);
  const [lookupDictionaries, setLookupDictionaries] = useState<
    LookupDictionary[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<LookupDictionaryFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedLookupDictionary, setSelectedLookupDictionary] =
    useState<LookupDictionary | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => lookupDictionaries.length === 0 && !loading,
    [lookupDictionaries.length, loading]
  );

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: LookupDictionary) => {
    setLookupDictionaries((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setLookupDictionaries((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setLookupDictionaries((prev) =>
      prev.filter((item) => !idsSet.has(item.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setLookupDictionary(null);
    setLookupDictionaries([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedLookupDictionary(null);
  }, []);

  // Get single lookup dictionary (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as LookupDictionary[])[0];
          setLookupDictionary(data);
          updateOrAddInList(data);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionary";
          setLastError(errorMessage);
          setLookupDictionary(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionary";
        setLastError(errorMessage);
        setLookupDictionary(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // List lookup dictionaries (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<LookupDictionaryFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      shouldReset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (shouldReset) {
          setLookupDictionaries([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await LookupDictionaryService.list(
          filter as LookupDictionaryFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as LookupDictionary[])
            : [];

          if (isAppending) {
            setLookupDictionaries((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setLookupDictionaries(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionaries";
          setLastError(errorMessage);
          if (!isAppending) setLookupDictionaries([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionaries";
        setLastError(errorMessage);
        if (!isAppending) setLookupDictionaries([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many lookup dictionaries by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<LookupDictionary[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as LookupDictionary[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionaries";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionaries";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Create lookup dictionary (matching API method name)
  const create = useCallback(
    async (params: LookupDictionaryInput): Promise<LookupDictionary | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as LookupDictionary[])[0];
          setLookupDictionary(newItem);
          setLookupDictionaries((prev) => [newItem, ...prev]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create lookup dictionary";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create lookup dictionary";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update lookup dictionary (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: LookupDictionaryInput
    ): Promise<LookupDictionary | null> => {
      if (!id) {
        setLastError("Lookup dictionary ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as LookupDictionary[])[0];
          setLookupDictionary(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update lookup dictionary";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update lookup dictionary";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove lookup dictionary (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Lookup dictionary ID is required for removal");
        return false;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await LookupDictionaryService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setLookupDictionary(null);
          removeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove lookup dictionary";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove lookup dictionary";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many lookup dictionaries (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError(
          "At least one lookup dictionary ID is required for removal"
        );
        return false;
      }

      setLastError(null);

      try {
        const result = await LookupDictionaryService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setLookupDictionary(null);
          removeManyFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove lookup dictionaries";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove lookup dictionaries";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeManyFromList]
  );

  // Load more lookup dictionaries
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh lookup dictionaries
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  return {
    lookupDictionary,
    lookupDictionaries,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingId,
    selectedLookupDictionary,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setSelectedLookupDictionary,
    reset,
    lastError,
    clearError,
  };
};
